import {
  ExecuteEqlQuery,
  GetRecordsResponseViewFragment,
  useExecuteEqlQuery,
} from 'generated/graphql';
import { useMemo } from 'react';
import { AndOfErrors } from 'v2/core/errors/errors';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import {
  AndStatuses,
  CreateSkipWithStatus,
  EdgeResponse,
  ExecuteEQLResponse,
  QueryOptions,
  QueryStatus,
  UseExecuteEQLInput,
} from 'v2/core/repository/edge-server/types';
import { useFetchMore } from 'v2/core/repository/edge-server/utils/records';
import { mapToEQL } from 'v2/core/repository/eql/mappers';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { useFetchEQLFields } from '@modules/common/hooks';

interface useExecuteEQLOptions {
  input: UseExecuteEQLInput;
  options?: QueryOptions;
  onCompleted?: (data: ExecuteEqlQuery) => void;
}

const useExecuteEQL = ({
  input,
  options,
  onCompleted,
}: useExecuteEQLOptions): EdgeResponse<ExecuteEQLResponse, true> => {
  // TODO: Refactor this. Ideally a gateway should only call one API to avoid
  // any business logic.
  const {
    data: sourceDetailsMap,
    status: sStatus,
    error: sError,
  } = useGetSourceDetailsMap();

  const { fields: eqlFields, isLoading: isLoadingEqlFields } =
    useFetchEQLFields();

  const eql = useMemo(
    () =>
      sourceDetailsMap
        ? mapToEQL({
            ...input.eqlProps,
            sourceDetailsMap,
            eqlFields,
          })
        : undefined,
    [input.eqlProps, eqlFields, sourceDetailsMap],
  );

  const skip = CreateSkipWithStatus(
    options?.Skip || !sourceDetailsMap || !eql,
    sStatus,
  );

  const {
    data,
    loading,
    error: eError,
    called,
    fetchMore: recordFetchMore,
    refetch,
  } = useExecuteEqlQuery({
    variables: {
      tenantID: input.tenantID,
      cursor: input.cursor,
      expression: eql ?? '',
    },
    /*https://github.com/apollographql/apollo-client/issues/6190#issuecomment-649933628*/
    fetchPolicy: skip ? 'cache-only' : options?.FetchPolicy,
    nextFetchPolicy: skip ? 'cache-only' : options?.NextFetchPolicy,
    skip,
    onCompleted,
  });

  const eStatus = apolloStatusToQueryStatus(loading, called, skip, eError);
  const eqlFieldsStatus = apolloStatusToQueryStatus(isLoadingEqlFields, true);

  const cursor = (data?.executeEQL?.result as GetRecordsResponseViewFragment)
    ?.cursor;
  const [isLoadingMore, fetchMore] = useFetchMore(recordFetchMore, cursor);

  const status = AndStatuses([sStatus, eStatus, eqlFieldsStatus]);
  const error = AndOfErrors([sError, eError]);

  return {
    data:
      data || status === QueryStatus.SUCCESS_COMPLETED
        ? data?.executeEQL?.result
        : undefined,
    error,
    fetchMore,
    isLoadingMore,
    status,
    hasMore: !!cursor,
    refetch,
  };
};

export default useExecuteEQL;
